import classNames from 'classnames';
import { ComponentPropsWithoutRef, ReactNode, useCallback, useState } from 'react';
import { attempt } from 'yoso';
import { DropdownPlacement } from '~/components/Dropdown/Dropdown.hooks';
import { CelectContext, CelectContextValue } from '~/components/Form/Celect/Celect.hooks';
import { useHandleBodyClick } from '~/components/Modal/Modal.hooks';
import styles from './CelectRoot.module.scss';

export type CelectRootProps = Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> & {
  value?: string;
  placement?: DropdownPlacement;
  closeOnClick?: boolean;
  onChange?: (value: string) => void;
  children?: ReactNode | ((context: CelectContextValue) => ReactNode);
};
export const CelectRoot = ({
  className,
  value,
  placement = 'bottom',
  closeOnClick = true,
  children,
  onChange,
  ...props
}: CelectRootProps) => {
  const [active, setActive] = useState(false);
  const open = useCallback(() => setActive(true), []);
  const close = useCallback(() => setActive(false), []);
  const toggle = useCallback(() => setActive((active) => !active), []);
  const { ref } = useHandleBodyClick({
    onClick: () => {
      close();
    },
  });
  const setValue = useCallback(
    (newValue: string) => {
      if (value !== newValue) {
        onChange?.(newValue);
      }
    },
    [onChange, value]
  );

  const context = {
    active,
    value,
    placement,
    open,
    close,
    closeOnClick,
    toggle,
    setValue,
    onChange,
  };
  return (
    <CelectContext.Provider value={context}>
      <div className={classNames(styles.root, className)} ref={ref} {...props}>
        {attempt(children, context)}
      </div>
    </CelectContext.Provider>
  );
};
